import { runpodComplete, stripJsonFence } from './runpodClient';
import { buildPdfExtractUserContent, PDF_EXTRACT_SYSTEM_PROMPT } from './pdfExtractPrompt';
import { normalizeExtractedQuote } from './normalizeExtractedQuote';
import { buildPdfShippingRows, parseShippingBaseCostsFromText } from './parseShippingFromText';
import {
  attachBoxShippingToPricing,
  DEFAULT_BOX_TURNAROUND,
  DEFAULT_LABEL_LEAD_TIME,
} from './quotePdfPayloadUtils';
import { getProductSpecsText } from './quotePdfImages';
import {
  defaultQuoteDateIso,
  defaultValidUntilIso,
} from './quotePdfDateUtils';

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatUnitPrice(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}`;
}

const SHIPPING_TIMEFRAMES = {
  'Premium Air shipping': '3-5 business days',
  'Express Air shipping': '4-5 business days',
  'Regular Air Shipping': '7-9 business days',
  'Express Sea shipping': '20-24 business days',
  'Regular Sea Shipping': '30-40 business days',
};

function buildLabelPricingFromItems(labelItems = []) {
  const rows = [];
  labelItems.forEach((item) => {
    const specsLine = item.specsLine || '';
    const skuLabel = item.skuLabel || '';
    const quantities = Array.isArray(item.quantities) ? item.quantities.map(Number) : [];
    const prices = Array.isArray(item.pricePerUnit) ? item.pricePerUnit.map(Number) : [];
    quantities.forEach((qty, index) => {
      const price = prices[index];
      rows.push({
        specsLine,
        skuLabel,
        qty,
        pricePerUnit: price,
        orderTotal: price != null && !Number.isNaN(price) ? qty * price : null,
      });
    });
  });
  return rows;
}

function buildStandardPricing(tiers, prices) {
  return tiers.map((qty, index) => {
    const price = prices[index];
    return {
      qty,
      pricePerUnit: price,
      orderTotal: price != null && !Number.isNaN(price) ? qty * price : null,
    };
  });
}

function normalizeRawProductEntries(raw) {
  if (Array.isArray(raw.products) && raw.products.length) {
    return raw.products.filter((p) => p && p.productType);
  }
  if (raw.productType) {
    return [{
      productType: raw.productType,
      styleNumber: raw.styleNumber,
      specsLine: raw.specsLine,
      material: raw.material,
      thickness: raw.thickness,
      labelShape: raw.labelShape,
      rollingDirection: raw.rollingDirection,
      specialEffects: raw.specialEffects,
      shippingMethod: raw.shippingMethod,
      loadingSide: raw.loadingSide,
      zipper: raw.zipper,
      finish: raw.finish,
      bagFeatures: raw.bagFeatures,
      widthInches: raw.widthInches,
      heightInches: raw.heightInches,
      gussetInches: raw.gussetInches,
      skuCount: raw.skuCount,
      quantityTiers: raw.quantityTiers,
      pricePerUnit: raw.pricePerUnit,
      labelItems: raw.labelItems,
      orderLeadTime: raw.orderLeadTime,
      shipping: raw.shipping,
    }];
  }
  return [];
}

function buildProductFromPdfExtract(raw, sourceText = '', { multiProduct = false } = {}) {
  const productType = raw.productType || 'Bags';
  const styleNumber = String(raw.styleNumber || raw.bagType || raw.boxType || '4');
  const isLabel = productType === 'Labels';

  const fields = {
    bagType: productType === 'Bags' ? styleNumber : '',
    boxType: productType === 'Boxes' ? styleNumber : '',
    material: raw.material || '',
    printingFinishes: raw.finish || '',
    closureType: raw.zipper ? 'Zipper' : '',
    zipperType: raw.zipper || '',
    bagSpecifications: Array.isArray(raw.bagFeatures) ? raw.bagFeatures : [],
    shape: raw.labelShape || '',
    thickness: raw.thickness || '',
    rollingDirection: raw.rollingDirection || '',
    specialEffects: raw.specialEffects || '',
    applyMethod: raw.applyMethod || '',
    shippingMethod: raw.shippingMethod || '',
  };

  if (raw.widthInches) fields.width = String(raw.widthInches);
  if (raw.heightInches) fields.height = String(raw.heightInches);
  if (raw.gussetInches) fields.gusset = String(raw.gussetInches);
  fields._dimensionsInInches = true;

  const skuCount = Math.max(1, parseInt(raw.skuCount, 10) || 1);
  const tiers = Array.isArray(raw.quantityTiers) ? raw.quantityTiers.map(Number).filter(Boolean) : [];
  const prices = Array.isArray(raw.pricePerUnit) ? raw.pricePerUnit.map(Number) : [];

  let pdfPricing = isLabel && Array.isArray(raw.labelItems) && raw.labelItems.length
    ? buildLabelPricingFromItems(raw.labelItems)
    : buildStandardPricing(tiers, prices);

  if (!pdfPricing.length && isLabel && raw.specsLine) {
    pdfPricing = buildStandardPricing(tiers, prices).map((row) => ({
      ...row,
      specsLine: raw.specsLine,
      skuLabel: String(skuCount),
    }));
  }

  const tierCount = pdfPricing.length || tiers.length || 3;

  const pdfShipping = buildPdfShippingRows(
    multiProduct ? '' : sourceText,
    raw.shipping || [],
    SHIPPING_TIMEFRAMES,
    tierCount,
  );

  const shippingFromText = !multiProduct
    && !raw.shipping?.length
    && parseShippingBaseCostsFromText(sourceText).length > 0;
  const showShippingTable = !isLabel && (pdfShipping.some((row) => row.baseCost != null) || shippingFromText);

  if (isLabel && !fields.shippingMethod && pdfShipping[0]?.method) {
    fields.shippingMethod = pdfShipping[0].method;
  }

  const skuDetails = Array.from({ length: skuCount }, (_, skuIndex) => ({
    sku: skuIndex + 1,
    quantities: tiers.map((val, qtyIndex) => ({
      quantity: qtyIndex + 1,
      value: val,
    })),
  }));

  let product = {
    productType,
    SKU: skuCount,
    quantitiesCount: pdfPricing.length || tiers.length || 1,
    skuDetails,
    fields,
    pdfSpecsLine: raw.specsLine || '',
    pdfPricing,
    pdfShipping: isLabel ? [] : pdfShipping,
    specialNotesToProduction: raw.projectDescription || '',
    showShippingTable,
    pdfTurnaround: productType === 'Boxes' && !showShippingTable ? DEFAULT_BOX_TURNAROUND : '',
    pdfOrderLeadTime: isLabel
      ? (raw.orderLeadTime ? `Order lead time: ${raw.orderLeadTime}` : DEFAULT_LABEL_LEAD_TIME)
      : '',
  };

  if (productType === 'Boxes') {
    product = attachBoxShippingToPricing(product);
  }

  return { product, showShippingTable, orderLeadTime: raw.orderLeadTime || '' };
}

function normalizeProductForPayload(product) {
  const legacyShape = normalizeExtractedQuote({
    companyName: '',
    projectDescription: '',
    products: [product],
  });
  const normalizedProduct = legacyShape.products[0] || product;
  return {
    ...normalizedProduct,
    pdfSpecsLine: product.pdfSpecsLine || normalizedProduct.pdfSpecsLine,
    pdfPricing: product.pdfPricing,
    pdfShipping: product.pdfShipping,
    showShippingTable: product.showShippingTable,
    pdfTurnaround: product.pdfTurnaround || '',
    pdfOrderLeadTime: product.pdfOrderLeadTime || '',
    fields: {
      ...normalizedProduct.fields,
      ...product.fields,
      _dimensionsInInches: true,
    },
  };
}

function buildInitialPayload(raw, productResults, overrides, pdfOutputMode = 'combined') {
  const products = productResults.map(({ product }) => normalizeProductForPayload(product));
  const first = products[0];

  return {
    companyName: overrides.companyName || raw.companyName || '',
    attentionTo: overrides.attentionTo || raw.attentionTo || '',
    salesRepName: overrides.salesRepName || '',
    projectId: overrides.projectId || raw.projectId || '',
    projectDescription: overrides.projectDescription || raw.projectDescription || '',
    quoteDate: overrides.quoteDate || defaultQuoteDateIso(),
    validUntil: overrides.validUntil || defaultValidUntilIso(),
    pdfOutputMode,
    showShippingTable: first?.showShippingTable !== false,
    pdfTurnaround: first?.pdfTurnaround || '',
    pdfOrderLeadTime: first?.pdfOrderLeadTime || '',
    pdfLayout: {
      logo: { x: 0, y: 0, w: 0, h: 0 },
      meta: { x: 0, y: 0, w: 0, h: 0 },
      styleSheet: { x: 0, y: 0, w: 0, h: 0 },
      details: { x: 0, y: 0, w: 0, h: 0 },
      productDescription: { x: 0, y: 0, w: 0, h: 0 },
      pricingTable: { x: 0, y: 0, w: 0, h: 0 },
      shippingTable: { x: 0, y: 0, w: 0, h: 0 },
      customTables: { x: 0, y: 0, w: 0, h: 0 },
    },
    pdfCustomTables: [],
    products,
  };
}

export async function buildPdfPayloadFromText(
  text,
  overrides = {},
  { pdfOutputMode = 'combined', userId = null, metadata = {} } = {},
) {
  const content = await runpodComplete({
    system: PDF_EXTRACT_SYSTEM_PROMPT,
    user: buildPdfExtractUserContent(text),
    maxTokens: 4096,
    feature: 'generate-quote-pdf',
    userId,
    metadata: {
      ...metadata,
      companyName: overrides.companyName || '',
      projectId: overrides.projectId || '',
      salesRepName: overrides.salesRepName || '',
    },
  });

  const raw = JSON.parse(stripJsonFence(content));
  if (!raw || typeof raw !== 'object') {
    throw new Error('Could not parse quote text.');
  }

  const entries = normalizeRawProductEntries(raw);
  if (!entries.length) {
    throw new Error('Could not detect a product type from the pasted text.');
  }

  const multiProduct = entries.length > 1;
  const productResults = entries.map((entry) =>
    buildProductFromPdfExtract(entry, text, { multiProduct }),
  );

  return buildInitialPayload(raw, productResults, overrides, pdfOutputMode);
}

function countSameTypeBefore(products, index) {
  const type = products[index]?.productType;
  if (!type) return index + 1;
  return products.slice(0, index + 1).filter((p) => p.productType === type).length;
}

function hasDuplicateProductType(products, productType) {
  return products.filter((p) => p.productType === productType).length > 1;
}

export function getProductSummaryLabel(product, index, products = []) {
  const type = product?.productType || 'Product';
  const specs = getProductSpecsText(product);
  const short = specs && specs !== type
    ? (specs.length > 48 ? `${specs.slice(0, 48)}…` : specs)
    : '';
  if (short) return `${type} — ${short}`;
  if (products.length && hasDuplicateProductType(products, type)) {
    return `${type} ${countSameTypeBefore(products, index)}`;
  }
  return `${type} ${index + 1}`;
}

/** Shorter label for product tabs when space is tight. */
export function getProductTabLabel(product, index, products = []) {
  const type = product?.productType || 'Product';
  const specs = getProductSpecsText(product);
  const specShort = specs && specs !== type
    ? (specs.length > 28 ? `${specs.slice(0, 28)}…` : specs)
    : '';
  if (products.length && hasDuplicateProductType(products, type)) {
    const n = countSameTypeBefore(products, index);
    return specShort ? `${type} ${n}: ${specShort}` : `${type} ${n}`;
  }
  return specShort ? `${type}: ${specShort}` : type;
}

export { formatMoney, formatUnitPrice };
